import './ButtonWithConfirm.scss';
import { FC, MouseEventHandler, ReactNode, useContext, useEffect, useId, useRef, useState } from 'react';
import { VisuallyHidden } from '@mantine/core';
import Button, { type ButtonTone, type ButtonVariant, type IButtonProps } from '../../atoms/Button';
import { confirmVariantOf } from '../../atoms/Button/confirmVariant';
import Preloader from '../../atoms/Preloader';
import { MenuContext } from '../../atoms/Menu/MenuContext';
import { useLatest } from '../../hooks/useLatest';
import { POPOVER_Z_INDEX } from '../../lib/zIndex';
import type { DropdownPosition } from '../../types/DropdownPosition';
import AnchoredDialog, { type IAnchoredDialogProps } from '../AnchoredDialog/AnchoredDialog';
import DoublePressButton from './DoublePressButton';

/**
 * Вид подтверждения: `popover` — вопрос у кнопки с «ОК» и «Отменой»; `double` — кнопка в два нажатия: первое взводит
 * её («Точно удалить?»), второе выполняет.
 */
export type ConfirmMode = 'popover' | 'double';

export interface IButtonWithConfirmProps extends IButtonProps {
    /** Текст подтверждения */
    confirmationText: ReactNode;
    /** Текст кнопки ОК */
    okButtonText?: string;
    /** Текст кнопки Отмена */
    cancelButtonText?: string;
    /** Событие открытия диалога подтверждения */
    onDialogOpen?: MouseEventHandler | undefined;
    /** Загрузка текста подтверждения */
    dialogLoading?: boolean;
    /** Положение вопроса относительно кнопки (в режиме `double` — подсказки) @default bottom-end, в `double` — bottom */
    confirmationPosition?: DropdownPosition;
    /** Вид кнопки ОК @default primary, с `okButtonTone` `success`/`danger` — light */
    okButtonVariant?: ButtonVariant;
    /** Тон кнопки ОК: необратимое действие (удаление) — `okButtonTone="danger"` (вид по умолчанию тогда `light`) */
    okButtonTone?: ButtonTone;
    /** Вопрос открылся или закрылся; в режиме `double` — кнопка взведена или сброшена */
    onOpenChange?: (open: boolean) => void;
    /** Вид подтверждения @default popover */
    mode?: ConfirmMode;
    /** `double`: надпись взведённой кнопки; по умолчанию «Точно <подпись кнопки>?» */
    armedText?: ReactNode;
    /**
     * `double`: пояснение подсказкой у взведённой кнопки — то, чего нет на ней самой (число затрагиваемых записей,
     * необратимость). Вопрос `confirmationText` в этом режиме не показывается: его заменяет надпись «Точно …?», а
     * скринридер объявляет его при взводе.
     */
    confirmationHint?: ReactNode;
    /** `double`: сколько кнопка ждёт второе нажатие, мс @default 3000 */
    confirmTimeout?: number;
}

interface IConfirmationProps extends Pick<IButtonProps, 'type' | 'form' | 'onClick'> {
    labelId: string;
    confirmationText: ReactNode;
    okButtonText: string;
    cancelButtonText: string;
    okButtonVariant: ButtonVariant;
    okButtonTone?: ButtonTone;
    dialogLoading: boolean;
}

type Placement = Pick<IAnchoredDialogProps, 'placement' | 'align' | 'sideAlign'>;

const placementOf = (position: DropdownPosition): Placement => {
    const [side, edge] = position.split('-') as [NonNullable<IAnchoredDialogProps['placement']>, string?];
    if (side === 'left' || side === 'right') {
        return { placement: side, sideAlign: edge === 'start' || edge === 'end' ? edge : 'center' };
    }
    return { placement: side, align: edge === 'start' ? 'left' : edge === 'end' ? 'right' : 'center' };
};

/** Содержимое вопроса: внутри AnchoredDialog, чтобы закрываться с возвратом фокуса на кнопку. */
const Confirmation: FC<IConfirmationProps> = ({
    labelId,
    confirmationText,
    okButtonText,
    cancelButtonText,
    okButtonVariant,
    okButtonTone,
    dialogLoading,
    type,
    form,
    onClick,
}) => {
    const { onClose, closeToTrigger } = useContext(MenuContext);
    const close = closeToTrigger ?? onClose;
    // Окно открылось, пока вопрос грузился, — диктор прочёл подпись кнопки; пришедший вопрос объявляем
    const [openedBusy] = useState(dialogLoading);

    return (
        <div className="rf-button-confirm__menu">
            {openedBusy && <VisuallyHidden role="status">{dialogLoading ? null : confirmationText}</VisuallyHidden>}
            {dialogLoading ? (
                <Preloader className="rf-button-confirm__label" size={24} />
            ) : (
                <div id={labelId} className="rf-button-confirm__label">
                    {confirmationText}
                </div>
            )}

            <div className="rf-button-confirm__buttons">
                <Button
                    variant={okButtonVariant}
                    tone={okButtonTone}
                    size="sm"
                    type={type}
                    aria-label={okButtonText}
                    // пока вопрос грузится, подтверждать нечего (как вторым нажатием в режиме double)
                    disabled={dialogLoading}
                    onClick={(event) => {
                        onClick?.(event);
                        close();
                    }}
                    form={form}
                >
                    {okButtonText}
                </Button>
                <Button variant="light" size="sm" aria-label={cancelButtonText} data-autofocus onClick={close}>
                    {cancelButtonText}
                </Button>
            </div>
        </div>
    );
};

/**
 * Кнопка с подтверждением действия. `popover` — вопрос у кнопки; `double` — кнопка в два нажатия
 * (`DoublePressButton`): взведённая кнопка — «Точно?», вопрос «Точно …?» — подсказкой у неё.
 */
const ButtonWithConfirm: FC<IButtonWithConfirmProps> = ({ mode = 'popover', ...props }: IButtonWithConfirmProps) =>
    mode === 'double' ? <DoublePressConfirm {...props} /> : <PopoverConfirm {...props} />;

type ModeProps = Omit<IButtonWithConfirmProps, 'mode'>;

const omitKeys = <T extends object, K extends keyof T>(source: T, keys: readonly K[]): Omit<T, K> => {
    const copy = { ...source };
    for (const key of keys) delete copy[key];
    return copy;
};

/**
 * Режим `double`: тон взведённой кнопки — по тону действия (кнопки или «ОК»: `danger` — красная заливка),
 * подсказка — по `confirmationPosition`; `onDialogOpen` — первое нажатие, `dialogLoading` — пояснение грузится.
 */
const DoublePressConfirm: FC<ModeProps> = (props) => {
    const {
        confirmationText,
        confirmationHint,
        armedText,
        confirmTimeout,
        dialogLoading = false,
        confirmationPosition = 'bottom',
        okButtonTone,
        onOpenChange,
        onDialogOpen,
        ...buttonProps
    } = omitKeys(props, ['okButtonText', 'cancelButtonText', 'okButtonVariant']);
    const danger = okButtonTone === 'danger' || buttonProps.tone === 'danger';

    return (
        <DoublePressButton
            {...buttonProps}
            armedText={armedText}
            confirmationText={confirmationText}
            confirmationHint={confirmationHint}
            armedTone={danger ? 'danger' : 'primary'}
            hintPosition={confirmationPosition}
            timeout={confirmTimeout}
            hintLoading={dialogLoading}
            onArm={onDialogOpen}
            onArmedChange={onOpenChange}
        />
    );
};

/**
 * Режим `popover`. При открытии фокус на «Отмене» при любом виде кнопки ОК: вопрос чаще всего стоит перед удалением,
 * и Enter, нажатый дважды, не должен выполнять действие. Вопрос — над окнами (кнопка бывает в подвале модалки);
 * Escape в нём закрывает только вопрос.
 */
const PopoverConfirm: FC<ModeProps> = (allProps) => {
    const {
        confirmationText,
        okButtonText = 'ОК',
        cancelButtonText = 'Отмена',
        dialogLoading = false,
        confirmationPosition = 'bottom-end',
        okButtonVariant,
        okButtonTone,
        onOpenChange,
        children,
        ...props
    } = omitKeys(allProps, ['confirmationHint', 'armedText', 'confirmTimeout']);
    const { type, form, onClick, onDialogOpen, ...restProps } = props;
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const labelId = useId();

    const openChange = useLatest(onOpenChange);
    const reportedOpen = useRef(false);
    useEffect(() => {
        if (reportedOpen.current === isDialogOpen) return;
        reportedOpen.current = isDialogOpen;
        openChange.current?.(isDialogOpen);
    }, [isDialogOpen, openChange]);

    return (
        <div className="rf-button-confirm">
            <AnchoredDialog
                {...placementOf(confirmationPosition)}
                opened={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                width={360}
                zIndex={POPOVER_Z_INDEX}
                popupRole="alertdialog"
                // пока текст вопроса грузится, окно подписано кнопкой-якорем
                dropdownAria={{
                    'aria-labelledby': dialogLoading ? undefined : labelId,
                    'aria-busy': dialogLoading || undefined,
                }}
                trapFocus
                target={
                    <Button
                        {...restProps}
                        aria-expanded={isDialogOpen}
                        aria-haspopup="dialog"
                        onClick={(event) => {
                            onDialogOpen?.(event);
                            if (!props.disabled) {
                                setIsDialogOpen(true);
                            }
                        }}
                    >
                        {children}
                    </Button>
                }
            >
                <Confirmation
                    labelId={labelId}
                    confirmationText={confirmationText}
                    okButtonText={okButtonText}
                    cancelButtonText={cancelButtonText}
                    okButtonVariant={okButtonVariant ?? confirmVariantOf(okButtonTone)}
                    okButtonTone={okButtonTone}
                    dialogLoading={dialogLoading}
                    type={type}
                    form={form}
                    onClick={onClick}
                />
            </AnchoredDialog>
        </div>
    );
};

export default ButtonWithConfirm;
